import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import moment from "moment";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Formik } from "formik";
import * as yup from "yup";
import { isValidPhoneNumber } from "react-phone-number-input";
import ConfirmationBox from "./ConfirmationBox.tsx";
import ClientSelectionFields from "./ClientSelectionFields.tsx";
import ConflictObserver from "./ConflictObserver.tsx";
import { madeList } from "./utlis.ts";
import ServiceApiService from "../services/service.service";
import TechnicianService from "../services/technician.service";
import UserService from "../services/user.service";
import type { ServiceItem } from "../pages/models";

const durationOptions: SelectOption<number>[] = Array.from(
  { length: 12 },
  (_, i) => ({
    value: i + 1,
    label: `${i + 1} hour${i + 1 > 1 ? "s" : ""}`,
  }),
);

type ServiceDisplayStatus =
  | "scheduled"
  | "confirmed"
  | "paid"
  | "done"
  | "client_not_show_up"
  | "canceled_rob"
  | "canceled_client"
  | "canceled_overtime";

type ServiceDetailProps = {
  service: ServiceItem | null | undefined;
  show: boolean;
  onHide: () => void;
  onServiceChange: () => void;
  companyShortName?: string;
};

type SelectOption<T extends string | number = string> = {
  value: T;
  label: T | string;
};

type ServiceFormValues = {
  email: string;
  client_name: string;
  phone_number: string;
  client: any;
  note: string;
  mark: SelectOption<string> | null;
  model: string;
  year: string;
  durationHours: SelectOption<number> | null;
  technician: SelectOption<string> | null;
  depositAmount: string;
  orderNumber: string;
  startDateTime: Date;
};

const statusBadge: Record<ServiceDisplayStatus, string> = {
  scheduled: "warning",
  confirmed: "danger",
  paid: "success",
  done: "info",
  client_not_show_up: "dark",
  canceled_rob: "secondary",
  canceled_client: "secondary",
  canceled_overtime: "secondary",
};

const getApiErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const apiError = error as {
      response?: {
        data?: {
          errors?: {
            nonFieldErrors?: string[];
            non_field_errors?: string[];
          } | string;
          message?: string;
        };
      };
      message?: string;
    };

    const errors = apiError.response?.data?.errors;
    const nonFieldErrors =
      typeof errors === "object" && errors !== null
        ? errors.nonFieldErrors?.[0] || errors.non_field_errors?.[0]
        : undefined;

    return (
      nonFieldErrors ||
      (typeof errors === "string" ? errors : undefined) ||
      apiError.response?.data?.message ||
      apiError.message ||
      "Unknown error"
    );
  }

  return "Unknown error";
};

const getRenderableError = (error: unknown): string | null =>
  typeof error === "string" ? error : null;

const ServiceDetail = ({
  service,
  show,
  onHide,
  onServiceChange,
  companyShortName = "RDS",
}: ServiceDetailProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditDisabled, setIsEditDisabled] = useState(true);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

  const selectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
      ...base,
      zIndex: 2000,
    }),
  };

  const validationSchema = yup.object().shape({
    client_name: yup
      .string()
      .required("Client name is required")
      .min(3, ({ min }) => `Client name must be at least ${min} characters`),
    email: yup
      .string()
      .email("Please enter valid e-mail")
      .required("E-mail is required"),
    phone_number: yup
      .string()
      .required("Phone number is required")
      .test("", "This not correct phone number", (value) =>
        isValidPhoneNumber(value || ""),
      ),
    year: yup
      .string()
      .matches(/^\d*$/, "Year can contain digits only")
      .test("year-max", "Year must be 2100 or less", (value) =>
        !value || Number(value) <= 2100,
      ),
    orderNumber: yup
      .string()
      .matches(/^\d*$/, "Order number can contain digits only"),
  });

  // Fetch technicians
  useEffect(() => {
    if (show) {
      TechnicianService.getTechnicians()
        .then((res) => {
          const list = res.data.data || [];
          const filtered = list.filter((t: any) => t.supports_service !== false && t.supportsService !== false);
          setTechnicians(filtered);
        })
        .catch(() => {});
      setIsEditDisabled(true);
    }
  }, [show]);

  if (!service) return null;

  const formatDate = () => moment(service.startDateTime).format("LLL");
  const activeStatuses = ["scheduled", "confirmed", "paid"] as const;
  const depositAmount = Number(service.depositAmount ?? 0);
  const hasCollectedDeposit = depositAmount > 0 && service.status === "paid";
  const statusLabel: Record<ServiceDisplayStatus, string> = {
    scheduled: "Scheduled",
    confirmed: "Confirmed",
    paid: "Paid",
    done: "Done",
    client_not_show_up: "No Show",
    canceled_rob: "Cancelled by service",
    canceled_client: "Cancelled by client",
    canceled_overtime: "Overtime Cancellation",
  };
  const statusKey = service.status as ServiceDisplayStatus;

  // Initialize Formik values
  const client = service.client;
  const initialValues: ServiceFormValues = {
    email: client?.email || "",
    client_name: client?.clientName || "",
    phone_number: client?.phoneNumber || client?.phone_number || "",
    client: client || null,
    note: service.note || "",
    model: service.model || "",
    mark: service.mark
      ? { value: service.mark, label: service.mark }
      : null,
    year: service.year ? String(service.year) : "",
    durationHours: service.durationHours
      ? { value: service.durationHours, label: `${service.durationHours} hours` }
      : null,
    technician: service.technician
      ? { value: service.technician.id, label: service.technician.name }
      : null,
    depositAmount: service.depositAmount ? String(service.depositAmount) : "",
    orderNumber: service.orderNumber || "",
    startDateTime: new Date(service.startDateTime),
  };

  const saveClientData = async (values: ServiceFormValues) => {
    const clientData = {
      clientName: values.client_name,
      email: values.email,
      phone_number: values.phone_number,
    };

    if (values.client && values.client.id) {
      await UserService.updateClient(values.client.id, clientData);
    }
  };

  const getApiErrorMessage = (error: unknown) => {
    const apiError = error as {
      response?: {
        status?: number;
        data?: unknown;
      };
      message?: string;
    };

    if (apiError.response?.status || apiError.response?.data) {
      return `${apiError.response.status ?? "Error"}: ${JSON.stringify(apiError.response.data)}`;
    }

    return apiError.message || "Unexpected error";
  };

  const handleSubmitForm = async (values: ServiceFormValues) => {
    setIsSubmitting(true);
    try {
      await saveClientData(values);

      const updateData = {
        startDateTime: values.startDateTime.toISOString(),
        durationHours: values.durationHours?.value,
        technician_id: values.technician?.value || null,
        deposit_amount: values.depositAmount || null,
        mark: values.mark?.value || null,
        model: values.model || null,
        year: values.year ? parseInt(values.year, 10) : null,
        order_number: values.orderNumber || null,
        note: values.note || null,
      };

      await ServiceApiService.updateService(service.id, updateData);
      onServiceChange();
      setIsEditDisabled(true);
    } catch (err) {
      alert("Error saving service: " + getApiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const technicianOptions: SelectOption<string>[] = technicians.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>
          Service &mdash; {formatDate()}{" "}
          <Badge bg={statusBadge[statusKey] || "secondary"}>
            {statusLabel[statusKey] || service.status}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ overflow: "visible" }}>
        {isEditDisabled && (
          <div className="mb-3 d-flex justify-content-end">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setIsEditDisabled(false)}
            >
              Edit Service Data
            </Button>
          </div>
        )}

        <Formik<ServiceFormValues>
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitForm}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form onSubmit={handleSubmit} noValidate>
              <ConflictObserver
                startDateTime={values.startDateTime}
                durationHours={values.durationHours?.value || 1}
                technicianId={values.technician?.value}
                type="service"
                setConflictWarning={setConflictWarning}
              />
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label>Date &amp; Time *</Form.Label>
                  <DatePicker
                    selected={values.startDateTime}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setFieldValue("startDateTime", date);
                      }
                    }}
                    showTimeSelect
                    timeIntervals={30}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    className="form-control"
                    wrapperClassName="w-100"
                    disabled={isEditDisabled}
                  />
                  {errors.startDateTime && touched.startDateTime && (
                    <div className="text-danger small">
                      {getRenderableError(errors.startDateTime)}
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <Form.Label>Duration *</Form.Label>
                  <Select<SelectOption<number>, false>
                    options={durationOptions}
                    value={values.durationHours}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => {
                      if (val) {
                        setFieldValue("durationHours", val);
                      }
                    }}
                    isDisabled={isEditDisabled}
                  />
                </div>

                {conflictWarning && (
                  <div className="col-12 mt-2">
                    <Alert variant="danger" className="mb-0">
                      ⚠️ {conflictWarning}
                    </Alert>
                  </div>
                )}

                <div className="col-12">
                  <Form.Label>Order Number</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="orderNumber"
                    value={values.orderNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isEditDisabled}
                  />
                  {errors.orderNumber && touched.orderNumber && (
                    <div className="text-danger small">
                      {getRenderableError(errors.orderNumber)}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <ClientSelectionFields
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isFormDisabled={isEditDisabled}
                    showAutocomplete={false}
                    showDetailsHeading={false}
                    initialClientLocked={true}
                    autocompletePlaceholder=""
                  />
                </div>

                <div className="col-md-6">
                  <Form.Label>Technician (optional)</Form.Label>
                  <Select<SelectOption<string>, false>
                    options={technicianOptions}
                    value={values.technician}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => setFieldValue("technician", val)}
                    isClearable
                    placeholder="Select technician..."
                    isDisabled={isEditDisabled}
                  />
                </div>

                <div className="col-md-6">
                  <Form.Label>Deposit Amount (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="$ Amount"
                    name="depositAmount"
                    value={values.depositAmount}
                    onChange={handleChange}
                    disabled={isEditDisabled}
                  />
                </div>

                <div className="col-md-4">
                  <Form.Label>Make</Form.Label>
                  <Select<SelectOption<string>, false>
                    options={madeList}
                    value={values.mark}
                    menuPortalTarget={menuPortalTarget}
                    styles={selectStyles}
                    onChange={(val) => setFieldValue("mark", val)}
                    isClearable
                    placeholder="Select make..."
                    isDisabled={isEditDisabled}
                  />
                </div>

                <div className="col-md-4">
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    name="model"
                    placeholder="e.g. CBR600"
                    value={values.model}
                    onChange={handleChange}
                    disabled={isEditDisabled}
                  />
                </div>

                <div className="col-md-4">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    name="year"
                    placeholder="e.g. 2022"
                    value={values.year}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isEditDisabled}
                  />
                  {errors.year && touched.year && (
                    <div className="text-danger small">
                      {getRenderableError(errors.year)}
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="note"
                    value={values.note}
                    onChange={handleChange}
                    placeholder="Any special notes..."
                    disabled={isEditDisabled}
                  />
                </div>
              </div>

              {!isEditDisabled && (
                <div className="d-flex gap-2 mt-4">
                  <Button variant="secondary" onClick={() => setIsEditDisabled(true)}>
                    Cancel Edit
                  </Button>
                  <Button variant="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? <Spinner size="sm" animation="border" /> : "Save Service Data"}
                  </Button>
                </div>
              )}
            </Form>
          )}
        </Formik>

        {isEditDisabled && (
          <div className="border-top mt-4 pt-3">
            <h5>Status &amp; Payment Operations</h5>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {activeStatuses.includes(statusKey) && (
                <>
                  <Button
                    variant="outline-danger"
                    onClick={async () => {
                      if (await ConfirmationBox("Are you sure you want to cancel the service?", "Yes", "No")) {
                        await ServiceApiService.cancelService(service.id, "canceled_client");
                        onServiceChange();
                      }
                    }}
                  >
                    Canceled by Client
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={async () => {
                      if (await ConfirmationBox("Are you sure you want to cancel the service?", "Yes", "No")) {
                        await ServiceApiService.cancelService(service.id, "canceled_rob");
                        onServiceChange();
                      }
                    }}
                  >
                    Canceled by Service
                  </Button>
                </>
              )}
              {statusKey === "scheduled" && (
                <Button
                  variant="outline-primary"
                  onClick={async () => {
                    await ServiceApiService.confirmService(service.id);
                    onServiceChange();
                  }}
                >
                  Confirm Service
                </Button>
              )}
              {statusKey === "confirmed" && (
                <Button
                  variant="outline-success"
                  onClick={async () => {
                    await ServiceApiService.payService(service.id);
                    onServiceChange();
                  }}
                >
                  Collect Deposit
                </Button>
              )}
              {statusKey === "paid" && (
                <Button
                  variant="outline-info"
                  onClick={async () => {
                    await ServiceApiService.doneService(service.id);
                    onServiceChange();
                  }}
                >
                  Mark as Done
                </Button>
              )}
              {hasCollectedDeposit && (
                <div className="w-100 mt-2 text-success small">
                  ✓ Deposit has been collected and processed successfully.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ServiceDetail;
